import { useCallback, useRef, useState } from "react";
import {
  FORMS_LIST_SORT_OPTIONS,
  parseSortOptionValue,
  toSortOptionValue,
  type FormsListPreferences,
  type FormsListSortBy,
  type FormsListSortDir,
} from "../../../lib/admin/formsListPreferences";
import { IconSearch, IconSliders, IconX } from "../../../components/icons";
import { useClickOutside } from "../../../hooks/useClickOutside";
import {
  type FormSourceFilter,
  type FormStatusFilter,
} from "../../../lib/admin/formListFilters";

const statusFilters: {
  value: FormStatusFilter;
  label: string;
}[] = [
  { value: "all", label: "Все" },
  { value: "open", label: "Открытые" },
  { value: "closed", label: "Закрытые" },
];

const sourceFilters: {
  value: FormSourceFilter;
  label: string;
}[] = [
  { value: "all", label: "Все источники" },
  { value: "native", label: "CHM" },
  { value: "yandex", label: "Яндекс" },
];

interface FormsListToolbarProps {
  preferences: FormsListPreferences;
  totalCount?: number;
  pageStats: { open: number; closed: number };
  onSearchChange: (search: string) => void;
  onStatusChange: (status: FormStatusFilter) => void;
  onSourceChange: (source: FormSourceFilter) => void;
  onSortChange: (sortBy: FormsListSortBy, sortDir: FormsListSortDir) => void;
  onResetFilters: () => void;
}

const controlClass =
  "h-8 rounded-lg border border-slate-200 bg-white font-onest text-sm text-slate-800 transition-colors focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20";

const selectClass = `${controlClass} w-full cursor-pointer py-0 pl-2.5 pr-8 text-sm leading-8`;

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <button
      type="button"
      title={`Снять фильтр «${label}»`}
      onClick={onRemove}
      className="inline-flex h-6 max-w-full items-center gap-1 rounded-full bg-slate-100 pl-2 pr-1 font-onest text-[11px] font-medium text-slate-700 hover:bg-slate-200/80"
    >
      <span className="min-w-0 truncate">{label}</span>
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-400 hover:text-slate-700">
        <IconX size={12} />
      </span>
    </button>
  );
}

export function FormsListToolbar({
  preferences,
  totalCount,
  pageStats,
  onSearchChange,
  onStatusChange,
  onSourceChange,
  onSortChange,
  onResetFilters,
}: FormsListToolbarProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);
  useClickOutside(filtersRef, closeFilters, filtersOpen);

  const search = preferences.search.trim();
  const statusLabel = statusFilters.find(
    (item) => item.value === preferences.status,
  )?.label;
  const sourceLabel = sourceFilters.find(
    (item) => item.value === preferences.source,
  )?.label;
  const chips = [
    search
      ? {
          key: "search",
          label: `«${search}»`,
          onRemove: () => onSearchChange(""),
        }
      : null,
    preferences.status !== "all" && statusLabel
      ? {
          key: "status",
          label: statusLabel,
          onRemove: () => onStatusChange("all"),
        }
      : null,
    preferences.source !== "all" && sourceLabel
      ? {
          key: "source",
          label: sourceLabel,
          onRemove: () => onSourceChange("all"),
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    onRemove: () => void;
  }>;
  const activeFilterCount = chips.filter(
    (chip) => chip.key !== "search",
  ).length;

  return (
    <div className="relative z-30 border-b border-slate-100 px-3 py-2 sm:px-4">
      <div className="flex items-center gap-2">
        <div className="relative w-1/2 min-w-0">
          <IconSearch
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={preferences.search}
            placeholder="Поиск…"
            onChange={(e) => onSearchChange(e.target.value)}
            className={`${controlClass} w-full py-0 pl-8 pr-8`}
          />
          {preferences.search ? (
            <button
              type="button"
              title="Очистить поиск"
              onClick={() => onSearchChange("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <IconX size={14} />
            </button>
          ) : null}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="hidden shrink-0 font-onest text-xs text-slate-500 sm:inline">
            {totalCount ?? "—"}
          </span>

          <div ref={filtersRef} className="relative shrink-0">
            <button
              type="button"
              aria-expanded={filtersOpen}
              aria-haspopup="dialog"
              title="Фильтры"
              onClick={() => setFiltersOpen((open) => !open)}
              className={[
                "inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 font-onest text-xs font-medium",
                activeFilterCount > 0
                  ? "border-brand-200 bg-brand-50 text-brand-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              ].join(" ")}
            >
              <IconSliders size={14} />
              <span className="hidden sm:inline">Фильтры</span>
              {activeFilterCount > 0 ? (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>

            {filtersOpen ? (
              <div
                role="dialog"
                aria-label="Фильтры списка форм"
                className="absolute right-0 z-50 mt-1.5 w-[16.5rem] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
              >
                <div className="space-y-2.5">
                  <label className="block">
                    <span className="mb-1 block font-onest text-[11px] text-slate-500">
                      Статус
                    </span>
                    <select
                      aria-label="Фильтр по статусу"
                      value={preferences.status}
                      onChange={(event) =>
                        onStatusChange(event.target.value as FormStatusFilter)
                      }
                      className={selectClass}
                    >
                      {statusFilters.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-1 block font-onest text-[11px] text-slate-500">
                      Источник
                    </span>
                    <select
                      aria-label="Фильтр по источнику"
                      value={preferences.source}
                      onChange={(event) =>
                        onSourceChange(event.target.value as FormSourceFilter)
                      }
                      className={selectClass}
                    >
                      {sourceFilters.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block md:hidden">
                    <span className="mb-1 block font-onest text-[11px] text-slate-500">
                      Сортировка
                    </span>
                    <select
                      aria-label="Сортировка"
                      value={toSortOptionValue(
                        preferences.sortBy,
                        preferences.sortDir,
                      )}
                      onChange={(event) => {
                        const parsed = parseSortOptionValue(event.target.value);
                        if (parsed) onSortChange(parsed.sortBy, parsed.sortDir);
                      }}
                      className={selectClass}
                    >
                      {FORMS_LIST_SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <p className="mt-3 font-onest text-[11px] text-slate-400">
                  {pageStats.open} откр. · {pageStats.closed} закр.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {chips.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              onRemove={chip.onRemove}
            />
          ))}
          <button
            type="button"
            onClick={onResetFilters}
            className="h-6 px-1 font-onest text-[11px] font-medium text-slate-500 hover:text-slate-800"
          >
            Сбросить
          </button>
        </div>
      ) : null}
    </div>
  );
}
